import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { detectDefaultTimezone, timezoneOptionsWithDefault } from "@/lib/timezones";

const MEETING_TYPES = ["Google Meet", "Zoom", "Microsoft Teams", "Phone", "In Person", "Other"];
const URL_TYPES = ["Google Meet", "Zoom", "Microsoft Teams"];
const DURATIONS = [30, 45, 60];

const fieldClass = "w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white";
const labelClass = "block text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 dark:text-slate-400";

const toLocalInputs = (existing, tz) => {
  if (!existing) return { date: "", time: "" };
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(existing.start_at));
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(existing.start_at));
  return { date: parts, time };
};

const buildSchedulePayload = ({ date, time, duration, tz, meetingType, meetingUrl, meetingLocation, assignedTo, internalNote }) => ({
  date,
  start_time: time,
  duration_minutes: Number(duration),
  timezone: tz,
  meeting_type: meetingType,
  meeting_url: URL_TYPES.includes(meetingType) || meetingType === "Other" ? meetingUrl.trim() || null : null,
  meeting_location: meetingType === "In Person" || meetingType === "Phone" ? meetingLocation.trim() || null : null,
  assigned_admin_id: assignedTo || null,
  internal_note: internalNote.trim() || null,
});

function MeetingDetailsFields({ meetingType, meetingUrl, setMeetingUrl, meetingLocation, setMeetingLocation }) {
  if (URL_TYPES.includes(meetingType) || meetingType === "Other") {
    return (
      <div className="sm:col-span-2">
        <label className={labelClass}>Meeting URL{meetingType === "Other" ? " (optional)" : ""}</label>
        <input
          type="text"
          required={meetingType !== "Other"}
          value={meetingUrl}
          onChange={(e) => setMeetingUrl(e.target.value)}
          placeholder="https://..."
          data-testid="schedule-meeting-url-input"
          className={fieldClass}
        />
      </div>
    );
  }
  if (meetingType === "In Person" || meetingType === "Phone") {
    return (
      <div className="sm:col-span-2">
        <label className={labelClass}>
          {meetingType === "In Person" ? "Meeting Location" : "Phone / Dial-in Instructions (optional)"}
        </label>
        <input
          type="text"
          required={meetingType === "In Person"}
          value={meetingLocation}
          onChange={(e) => setMeetingLocation(e.target.value)}
          placeholder={meetingType === "In Person" ? "Office address" : "Phone number or instructions"}
          data-testid="schedule-meeting-location-input"
          className={fieldClass}
        />
      </div>
    );
  }
  return null;
}

export default function ScheduleDemoForm({ demoRequestId, existing, mode, onCancel, onSaved }) {
  const { API, user } = useAuth();
  const timezone = existing?.timezone || detectDefaultTimezone();
  const initial = toLocalInputs(existing, timezone);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [duration, setDuration] = useState(existing?.duration_minutes || 30);
  const [tz, setTz] = useState(timezone);
  const [meetingType, setMeetingType] = useState(existing?.meeting_type || "Google Meet");
  const [meetingUrl, setMeetingUrl] = useState(existing?.meeting_url || "");
  const [meetingLocation, setMeetingLocation] = useState(existing?.meeting_location || "");
  const [assignedTo, setAssignedTo] = useState(existing?.assigned_admin_id || user?.user_id || "");
  const [internalNote, setInternalNote] = useState("");
  const [admins, setAdmins] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/platform-admins`, { withCredentials: true })
      .then(({ data }) => setAdmins(data.admins || []))
      .catch(() => {});
  }, [API]);

  const today = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    const payload = buildSchedulePayload({ date, time, duration, tz, meetingType, meetingUrl, meetingLocation, assignedTo, internalNote });
    try {
      const { data } = mode === "reschedule"
        ? await axios.patch(`${API}/admin/demo-requests/${demoRequestId}/schedule`, payload, { withCredentials: true })
        : await axios.post(`${API}/admin/demo-requests/${demoRequestId}/schedule`, payload, { withCredentials: true });
      onSaved(data.demo_request);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[0.03]" data-testid="schedule-demo-form">
      <h3 className="text-sm font-bold text-slate-950 dark:text-white">{mode === "reschedule" ? "Reschedule Demo" : "Schedule Demo"}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Date</label>
          <input
            type="date"
            required
            min={today}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="schedule-date-input"
            className={fieldClass}
          />
        </div>
        <div>
          <label className={labelClass}>Start Time</label>
          <input
            type="time"
            required
            value={time}
            onChange={(e) => setTime(e.target.value)}
            data-testid="schedule-time-input"
            className={fieldClass}
          />
        </div>
        <div>
          <label className={labelClass}>Duration</label>
          <select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            data-testid="schedule-duration-select"
            className={fieldClass}
          >
            {DURATIONS.map((d) => <option key={d} value={d}>{d} minutes</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Timezone</label>
          <select
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            data-testid="schedule-timezone-select"
            className={fieldClass}
          >
            {timezoneOptionsWithDefault(tz).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Meeting Type</label>
          <select
            value={meetingType}
            onChange={(e) => setMeetingType(e.target.value)}
            data-testid="schedule-meeting-type-select"
            className={fieldClass}
          >
            {MEETING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Assigned To</label>
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            data-testid="schedule-assigned-select"
            className={fieldClass}
          >
            {admins.length === 0 && user && <option value={user.user_id}>{user.name || user.email} (you)</option>}
            {admins.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}{a.user_id === user?.user_id ? " (you)" : ""}</option>)}
          </select>
        </div>

        <MeetingDetailsFields
          meetingType={meetingType}
          meetingUrl={meetingUrl}
          setMeetingUrl={setMeetingUrl}
          meetingLocation={meetingLocation}
          setMeetingLocation={setMeetingLocation}
        />

        <div className="sm:col-span-2">
          <label className={labelClass}>Internal Note (optional, not visible to the requester)</label>
          <textarea
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            data-testid="schedule-internal-note-input"
            className={`min-h-20 ${fieldClass}`}
          />
        </div>
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="schedule-form-error">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          data-testid="schedule-form-submit"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700 disabled:opacity-50"
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          {mode === "reschedule" ? "Save changes" : "Confirm schedule"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          data-testid="schedule-form-cancel"
          className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-white disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
